import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Keyboard, X } from 'lucide-react'
import {
  useDecisions,
  useApproveDecision,
  useRejectDecision,
  useApproveDecisions,
  useRejectDecisions,
} from '@/hooks/useDecisions'
import { useDecisionKeyboard } from '@/hooks/useDecisionKeyboard'
import { useDecisionSelection } from '@/hooks/useDecisionSelection'
import { groupDecisionsByType, orderedPendingIds, isFyi } from '@/lib/decision-utils'
import { DecisionGroup } from '@/components/decisions/DecisionGroup'
import { AcknowledgeSection } from '@/components/decisions/AcknowledgeSection'
import { SelectionBar } from '@/components/decisions/SelectionBar'
import { RejectDialog } from '@/components/decisions/RejectDialog'
import type { Decision, DecisionStatus } from '@/lib/api'

type Tab = 'PENDING' | 'APPROVED' | 'REJECTED'

const TABS: { key: Tab; label: string; countKey: 'pending' | 'approved' | 'rejected' }[] = [
  { key: 'PENDING', label: 'Pending', countKey: 'pending' },
  { key: 'APPROVED', label: 'Accepted', countKey: 'approved' },
  { key: 'REJECTED', label: 'Rejected', countKey: 'rejected' },
]

export function DecisionQueue() {
  const [tab, setTab] = useState<Tab>('PENDING')
  const [searchParams, setSearchParams] = useSearchParams()
  const agentFilter = searchParams.get('agent')
  const focusParam = searchParams.get('focus')

  const { data, isLoading, error, isFetching } = useDecisions(tab as DecisionStatus)
  const approve = useApproveDecision()
  const reject = useRejectDecision()
  const approveBatch = useApproveDecisions()
  const rejectBatch = useRejectDecisions()

  // Cards animating out after an approve/reject, until the refetch removes them.
  const [exitingIds, setExitingIds] = useState<Set<string>>(new Set())
  const [rejectTarget, setRejectTarget] = useState<Decision | null>(null)

  const counts = data?.counts ?? { pending: 0, approved: 0, rejected: 0 }

  const rawDecisions = data?.data ?? []
  const decisions = agentFilter
    ? rawDecisions.filter((d) => d.skillName === agentFilter)
    : rawDecisions

  // Split actionable decisions from FYI / informational ones so the latter never
  // pollute the queue — they get their own quiet Acknowledge section.
  const fyiDecisions = useMemo(() => decisions.filter(isFyi), [decisions])
  const actionable = useMemo(() => decisions.filter((d) => !isFyi(d)), [decisions])

  const groups = useMemo(() => groupDecisionsByType(actionable), [actionable])
  const pendingIds = orderedPendingIds(groups)
  const selection = useDecisionSelection(pendingIds)
  const batchPending = approveBatch.isPending || rejectBatch.isPending

  // Prune exiting ids that no longer exist in the current list (post-refetch).
  useEffect(() => {
    setExitingIds((prev) => {
      if (prev.size === 0) return prev
      const present = new Set(decisions.map((d) => d.id))
      const next = new Set([...prev].filter((id) => present.has(id)))
      return next.size === prev.size ? prev : next
    })
  }, [decisions])

  function markExiting(ids: string[]) {
    setExitingIds((prev) => {
      const next = new Set(prev)
      ids.forEach((id) => next.add(id))
      return next
    })
  }

  function handleApprove(id: string) {
    markExiting([id])
    approve.mutate(id)
  }

  function requestReject(id: string) {
    const target = decisions.find((d) => d.id === id) ?? null
    setRejectTarget(target)
  }

  function confirmReject(id: string, reason: string) {
    markExiting([id])
    reject.mutate({ id, reason: reason || undefined })
    setRejectTarget(null)
  }

  function approveAll(ids: string[]) {
    markExiting(ids)
    approveBatch.mutate(ids)
    selection.setMany(ids, false)
  }

  function rejectAll(ids: string[]) {
    markExiting(ids)
    rejectBatch.mutate({ ids })
    selection.setMany(ids, false)
  }

  function approveSelected() {
    approveAll([...selection.selected])
  }

  function rejectSelected() {
    rejectAll([...selection.selected])
  }

  const { focusedId, setFocusedId } = useDecisionKeyboard({
    pendingIds,
    onApprove: handleApprove,
    onReject: requestReject,
  })

  // Deep-link: ?focus=<id> from the dashboard "Review" links. Focus the card,
  // let its cluster expand, scroll to it, then drop the param so it's one-shot.
  useEffect(() => {
    if (!focusParam) return
    if (pendingIds.includes(focusParam)) setFocusedId(focusParam)
    const t = setTimeout(() => {
      document.getElementById(`decision-${focusParam}`)?.scrollIntoView({ block: 'center' })
    }, 120)
    const next = new URLSearchParams(searchParams)
    next.delete('focus')
    setSearchParams(next, { replace: true })
    return () => clearTimeout(t)
    // Only react to the focus param landing; other deps are stable enough here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusParam, pendingIds.length])

  function clearAgentFilter() {
    const next = new URLSearchParams(searchParams)
    next.delete('agent')
    setSearchParams(next, { replace: true })
  }

  const showKeyboardHint = tab === 'PENDING' && pendingIds.length > 0
  const showSelectionBar = tab === 'PENDING' && selection.selectedCount > 0
  const nothingToShow = groups.length === 0 && fyiDecisions.length === 0

  return (
    <div className="space-y-6">
      {/* Tabs with counts */}
      <div className="flex flex-wrap items-center justify-between gap-2 bg-cmd-container p-2">
        <div className="flex flex-wrap items-center gap-1">
          {TABS.map((t) => (
            <Button
              key={t.key}
              variant={tab === t.key ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setTab(t.key)}
              className="font-label text-xs tracking-wider"
              aria-pressed={tab === t.key}
            >
              {t.label}
              <span className="ml-1.5 font-mono tabular-nums opacity-70">({counts[t.countKey]})</span>
            </Button>
          ))}
        </div>
        {showKeyboardHint && (
          <div className="flex items-center gap-1.5 px-2 font-mono text-[0.6rem] text-cmd-outline">
            <Keyboard className="h-3.5 w-3.5" aria-hidden="true" />
            <span>
              <span className="text-primary">A</span> approve ·{' '}
              <span className="text-primary">R</span> reject ·{' '}
              <span className="text-primary">N</span> next
            </span>
          </div>
        )}
      </div>

      {/* Multi-select batch bar */}
      {showSelectionBar && (
        <SelectionBar
          selectedCount={selection.selectedCount}
          visibleCount={pendingIds.length}
          allVisibleSelected={selection.allSelected(pendingIds)}
          someVisibleSelected={selection.someSelected(pendingIds)}
          onToggleSelectAll={(c) => selection.setMany(pendingIds, c)}
          onApprove={approveSelected}
          onReject={rejectSelected}
          onClear={selection.clear}
          busy={batchPending}
        />
      )}

      {/* Agent deep-link filter chip */}
      {agentFilter && (
        <div className="flex items-center gap-2 bg-cmd-low px-3 py-2">
          <span className="font-label text-[0.65rem] tracking-wider text-cmd-outline uppercase">
            Filtered to agent
          </span>
          <span className="font-mono text-xs text-primary">{agentFilter}</span>
          <Button
            size="sm"
            variant="ghost"
            onClick={clearAgentFilter}
            className="ml-auto font-label text-[0.6rem] tracking-wider"
          >
            <X className="mr-1 h-3 w-3" aria-hidden="true" />
            CLEAR
          </Button>
        </div>
      )}

      {error && (
        <div className="bg-cmd-error-container p-4">
          <p className="text-sm text-cmd-on-error font-label">FAULT DETECTED: {error.message}</p>
        </div>
      )}

      {(approveBatch.data?.failed || rejectBatch.data?.failed) ? (
        <div className="bg-[#3d2e00] p-3">
          <p className="text-xs text-[#ffd080] font-label">
            BATCH PARTIAL: {(approveBatch.data?.failed ?? 0) + (rejectBatch.data?.failed ?? 0)} action(s)
            failed and were left pending.
          </p>
        </div>
      ) : null}

      {/* Decision groups + FYI section */}
      {isLoading ? (
        <div className="space-y-1">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-5 space-y-3">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
                <div className="flex gap-2 pt-2">
                  <Skeleton className="h-8 w-20" />
                  <Skeleton className="h-8 w-20" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : nothingToShow ? (
        <Card>
          <CardContent className="py-8 text-center">
            <p className="font-label text-sm text-cmd-outline">
              {emptyMessage(tab, !!agentFilter)}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className={`space-y-5 transition-opacity ${isFetching ? 'opacity-70' : ''}`}>
          {groups.map((group) => (
            <DecisionGroup
              key={group.type}
              group={group}
              focusedId={focusedId}
              onFocus={setFocusedId}
              onApprove={handleApprove}
              onReject={requestReject}
              approving={approve.isPending}
              rejecting={reject.isPending}
              onApproveAll={approveAll}
              onRejectAll={rejectAll}
              batchPending={batchPending}
              exitingIds={exitingIds}
              selection={selection}
            />
          ))}

          <AcknowledgeSection
            decisions={fyiDecisions}
            onAcknowledge={handleApprove}
            onAcknowledgeAll={approveAll}
            busy={approve.isPending || batchPending}
            exitingIds={exitingIds}
          />
        </div>
      )}

      <RejectDialog
        decision={rejectTarget}
        busy={reject.isPending}
        onConfirm={confirmReject}
        onCancel={() => setRejectTarget(null)}
      />
    </div>
  )
}

function emptyMessage(tab: Tab, filtered: boolean): string {
  if (filtered) return 'NO DECISIONS FOR THIS AGENT'
  switch (tab) {
    case 'PENDING': return 'NO PENDING DECISIONS — you are all caught up'
    case 'APPROVED': return 'NO ACCEPTED DECISIONS YET'
    case 'REJECTED': return 'NO REJECTED DECISIONS'
  }
}
